import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { authFetch } from "../services/authService";
import { 
  LayoutDashboard, 
  Users, 
  Tag, 
  MessageSquare,
  LogOut,
  Wrench,
  ArrowLeft,
  Award,
  FileText,
  Link2,
  Settings,
  Zap,
  HelpCircle,
  BookOpen,
  Youtube,
  DatabaseBackup,
  BarChart3
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Menu items con badge keys
const navItems = [
  { path: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, badgeKey: null },
  { path: "/admin/users", icon: Users, label: "Utenti", badgeKey: "pendingUsers" },
  { path: "/admin/ricambisti", icon: Link2, label: "Ricambisti/Officine", badgeKey: null },
  { path: "/admin/preventivo-veloce", icon: Zap, label: "Richiesta Rapida Ricambi", badgeKey: null },
  { path: "/admin/support-requests", icon: HelpCircle, label: "Richieste Assistenza", badgeKey: "supportRequests" },
  { path: "/admin/modules", icon: Settings, label: "Gestione Moduli", badgeKey: null },
  { path: "/admin/youtube-channels", icon: Youtube, label: "Canali YouTube", badgeKey: null },
  { path: "/admin/campaigns", icon: Award, label: "Campagne Punti", badgeKey: null },
  { path: "/admin/brands", icon: Tag, label: "Marche Sponsorizzate", badgeKey: null },
  { path: "/admin/communications", icon: MessageSquare, label: "Comunicazioni", badgeKey: null },
  { path: "/admin/quote-stats", icon: BarChart3, label: "Statistiche Preventivi", badgeKey: null },
  { path: "/admin/backups", icon: DatabaseBackup, label: "Backup & GDPR", badgeKey: "gdprPending" },
  { path: "/admin/audit", icon: FileText, label: "Audit Log", badgeKey: null },
];

export const AdminLayout = ({ children, user }) => {
  const navigate = useNavigate();
  const [menuBadges, setMenuBadges] = useState({
    pendingUsers: 0,
    supportRequests: 0,
    gdprPending: 0
  });

  useEffect(() => {
    fetchMenuBadges();
    // Poll every 30 seconds
    const interval = setInterval(fetchMenuBadges, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchMenuBadges = async () => {
    try {
      // Fetch pending users count and support requests in parallel
      const [usersRes, supportRes, gdprRes] = await Promise.all([
        authFetch(`${API}/admin/users/pending-count`, { }),
        authFetch(`${API}/admin/support-requests/pending-count`, { }),
        authFetch(`${API}/admin/gdpr/pending-count`, { })
      ]);
      
      let newBadges = { pendingUsers: 0, supportRequests: 0, gdprPending: 0 };
      
      if (usersRes.ok) {
        const data = await usersRes.json();
        newBadges.pendingUsers = data.count || 0;
      } else {
        // Fallback: fetch all users and count inactive ones
        const allUsersRes = await authFetch(`${API}/admin/users`, { });
        if (allUsersRes.ok) {
          const users = await allUsersRes.json();
          newBadges.pendingUsers = users.filter(u => u.status === 'pending' || u.is_active === false).length;
        }
      }
      
      if (supportRes.ok) {
        const data = await supportRes.json();
        newBadges.supportRequests = data.count || 0;
      }
      
      if (gdprRes.ok) {
        const data = await gdprRes.json();
        newBadges.gdprPending = data.count || 0;
      }
      
      setMenuBadges(newBadges);
    } catch (error) {
      console.error("Error fetching menu badges:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, {
        method: "POST"
      });
      toast.success("Logout effettuato");
      navigate("/");
    } catch (error) {
      toast.error("Errore durante il logout");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="sidebar">
        {/* Logo */}
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-sm flex items-center justify-center">
              <Wrench className="w-6 h-6 text-white" />
            </div>
            <div className="hidden md:block">
              <span className="text-xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed']">
                Admin
              </span>
              <p className="text-xs text-zinc-500">MechaniX Manager</p>
            </div>
          </div>
        </div>

        {/* Back to App */}
        <div className="p-2 border-b border-zinc-800">
          <NavLink
            to="/dashboard"
            className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-900/50 rounded-sm transition-colors"
            data-testid="back-to-app"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm hidden md:block">Torna all'App</span>
          </NavLink>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map((item) => {
            const badgeCount = item.badgeKey ? menuBadges[item.badgeKey] : 0;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""}`
                }
                data-testid={`admin-nav-${item.path.replace("/admin", "").replace("/", "") || "dashboard"}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="hidden md:block flex-1">{item.label}</span>
                {badgeCount > 0 && (
                  <span className="ml-auto min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-medium">
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-zinc-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                data-testid="admin-user-menu-btn"
                className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-zinc-900/50 transition-colors"
              >
                <Avatar className="w-10 h-10 border border-red-600">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-red-900 text-white">
                    {user?.name?.[0]?.toUpperCase() || "A"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left hidden md:block">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-red-400">Amministratore</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800">
              <DropdownMenuItem 
                onClick={() => navigate("/dashboard")}
                className="cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna all'App
              </DropdownMenuItem>
              <DropdownMenuItem 
                data-testid="admin-download-manual-btn"
                onClick={async () => {
                  try {
                    const response = await authFetch(`${API}/manual/download`);
                    if (response.ok) {
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'PService_Pro_Manuale_Utente.pdf';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                    }
                  } catch (err) { console.error(err); }
                }}
                className="cursor-pointer"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Scarica Manuale
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="cursor-pointer text-red-400"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
